import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { VehicleModalService } from 'src/app/services/vehicle-modal/vehicle-modal.service';
import { ExportCertificateService } from 'src/app/services/export-certificate/export-certificate.service';
import { ExportCertificateModalService } from 'src/app/services/export-certificate-modal/export-certificate-modal.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-vh-manager-export-certificate',
  templateUrl: './vh-manager-export-certificate.component.html',
  styleUrls: ['./vh-manager-export-certificate.component.scss']
})
export class VhManagerExportCertificateComponent extends DestructorComponent implements OnInit, OnDestroy {

  @Output() certificateGenerated = new EventEmitter<any>();
  @Output() certificateAdded = new EventEmitter<void>();
  @Output() certificateUpdated = new EventEmitter<void>();

  exportCertificateForm!: FormGroup;
  selectedVehicle: any = null;
  selectedCertificate: any = null;
  isLoading = false;
  isVehiclePreSelected: boolean = false;
  isEditMode: boolean = false;

  // Vehicle search properties
  vehicleSearchTerm: string = '';
  vehicleSearchResults: any[] = [];
  showVehicleDropdown: boolean = false;
  isVehicleSearching: boolean = false;
  private vehicleSearchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private _vehicleModalService: VehicleModalService,
    private _exportCertificateService: ExportCertificateService,
    private _exportCertificateModalService: ExportCertificateModalService,
    private _vehicleService: VehicleService
  ) {
    super();
    this.initializeForm();
    this.setupVehicleSearchDebounce();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.exportCertificateForm = this.fb.group({
      // Row 1
      certificate_no: ['', [Validators.required]],
      reference_number: [''],

      // Row 2
      phone_no: [''],

      // Row 3
      fax: [''],

      // Row 4
      registration_no: [''],
      registration_date: [''],
      first_reg_date: [''],
      makers_serial_number: [''],

      // Row 5
      trademark_maker: [''],
      model: [''],
      engine_model: [''],

      // Row 6
      name_of_owner: [''],

      // Row 7
      address_of_owner: [''],

      // Row 8
      name_of_user: [''],

      // Row 9
      address_of_user: [''],

      // Row 10
      locality_principal_abode: [''],

      // Row 11
      classification_of_vehicle: [''],
      use: [''],
      purpose: [''],
      type_of_body: [''],
      fixed_number: [''],
      maximum_carry: [''],
      weight: [''],
      g_weight: [''],

      // Row 12
      engine_capacity: [''],
      classification_of_fuel: [''],
      specification_no: [''],
      classification_no: [''],
      length: [''],
      width: [''],
      height: [''],
      f_weight: [''],
      fr_weight: [''],
      rf_weight: [''],
      r_weight: [''],

      // Row 13
      export_scheduled_day: [''],

      // Remarks section
      remarks: [''],
      old_vehicle_registration_no: [''],
      latest_owner_name_registration: [''],
      date_of_application: [''],

      // Final date
      final_date: ['']
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_vh_manager_export_certificate');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        // Check if we're editing an existing certificate
        const selectedCertificate = this._exportCertificateModalService.getSelectedCertificate();
        if (selectedCertificate) {
          this.isEditMode = true;
          this.selectedCertificate = selectedCertificate;
          this.selectedVehicle = selectedCertificate.vehicle;
          this.isVehiclePreSelected = true;
          this.vehicleSearchTerm = this.getVehicleDisplayName(selectedCertificate.vehicle);
          this.populateFormForEdit(selectedCertificate);
        } else {
          // Check if we have a pre-selected vehicle for add mode
          const serviceVehicle = this._vehicleModalService.getSelectedVehicle();
          if (serviceVehicle) {
            this.isEditMode = false;
            this.selectedVehicle = serviceVehicle;
            this.isVehiclePreSelected = true;
            this.vehicleSearchTerm = this.getVehicleDisplayName(serviceVehicle);
            this.getCertificateByVehicleId(serviceVehicle._id || serviceVehicle.id);
            
          } else {
            this.isEditMode = false;
            this.selectedVehicle = null;
            this.isVehiclePreSelected = false;
            this.vehicleSearchTerm = '';
            this.resetForm();
          }
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._vehicleModalService.clearSelectedVehicle();
        this._exportCertificateModalService.clearSelectedCertificate();
        this.resetForm();
        this.resetVehicleSearch();
      });
    }
  }

  private populateForm(vehicle: any): void {
    if (!vehicle) return;

    const currentDate = new Date();

    this.exportCertificateForm.patchValue({
      // Row 1
      certificate_no: `EXP-${vehicle.car_no || vehicle.inventory_no || ''}`,
      reference_number: vehicle.reference_number || '',

      // Row 2
      phone_no: '',

      // Row 3
      fax: '',

      // Row 4
      registration_no: vehicle.car_no || '',
      registration_date: vehicle.reg_year && vehicle.reg_month ?
        `${vehicle.reg_year}/${vehicle.reg_month}` : '',
      first_reg_date: vehicle.year && vehicle.month ?
        `${vehicle.year}/${vehicle.month}` : '',
      makers_serial_number: vehicle.chassis_no || '',

      // Row 5
      trademark_maker: vehicle.make || '',
      model: vehicle.model || '',
      engine_model: vehicle.engine || '',

      // Row 6
      name_of_owner: '',

      // Row 7
      address_of_owner: '',

      // Row 8
      name_of_user: '',

      // Row 9
      address_of_user: '',

      // Row 10
      locality_principal_abode: '',

      // Row 11
      classification_of_vehicle: '',
      use: '',
      purpose: '',
      type_of_body: vehicle.body_type || '',
      fixed_number: '',
      maximum_carry: vehicle.seat_capacity || '',
      weight: '',
      g_weight: '',

      // Row 12
      engine_capacity: '',
      classification_of_fuel: vehicle.fuel_type || '',
      specification_no: '',
      classification_no: '',
      length: '',
      width: '',
      height: '',
      f_weight: '',
      fr_weight: '',
      rf_weight: '',
      r_weight: '',

      // Row 13
      export_scheduled_day: '',

      // Remarks section
      remarks: '',
      old_vehicle_registration_no: '',
      latest_owner_name_registration: '',
      date_of_application: '',

      // Final date
      final_date: currentDate.toISOString().split('T')[0]
    });
  }

  private populateFormForEdit(certificate: any): void {
    if (!certificate) return;

    this.exportCertificateForm.patchValue({
      certificate_no: certificate.certificate_no || '',
      reference_number: certificate.reference_number || '',
      phone_no: certificate.phone_no || '',
      fax: certificate.fax || '',
      registration_no: certificate.registration_no || '',
      registration_date: certificate.registration_date || '',
      first_reg_date: certificate.first_reg_date || '',
      makers_serial_number: certificate.makers_serial_number || '',
      trademark_maker: certificate.trademark_maker || '',
      model: certificate.model || '',
      engine_model: certificate.engine_model || '',
      name_of_owner: certificate.name_of_owner || '',
      address_of_owner: certificate.address_of_owner || '',
      name_of_user: certificate.name_of_user || '',
      address_of_user: certificate.address_of_user || '',
      locality_principal_abode: certificate.locality_principal_abode || '',
      classification_of_vehicle: certificate.classification_of_vehicle || '',
      use: certificate.use || '',
      purpose: certificate.purpose || '',
      type_of_body: certificate.type_of_body || '',
      fixed_number: certificate.fixed_number || '',
      maximum_carry: certificate.maximum_carry || '',
      weight: certificate.weight || '',
      g_weight: certificate.g_weight || '',
      engine_capacity: certificate.engine_capacity || '',
      classification_of_fuel: certificate.classification_of_fuel || '',
      specification_no: certificate.specification_no || '',
      classification_no: certificate.classification_no || '',
      length: certificate.length || '',
      width: certificate.width || '',
      height: certificate.height || '',
      f_weight: certificate.f_weight || '',
      fr_weight: certificate.fr_weight || '',
      rf_weight: certificate.rf_weight || '',
      r_weight: certificate.r_weight || '',
      export_scheduled_day: certificate.export_scheduled_day || '',
      remarks: certificate.remarks || '',
      old_vehicle_registration_no: certificate.old_vehicle_registration_no || '',
      latest_owner_name_registration: certificate.latest_owner_name_registration || '',
      date_of_application: certificate.date_of_application || '',
      final_date: certificate.final_date ? certificate.final_date.split('T')[0] : ''
    });
  }

  private getCertificateByVehicleId(vehicleId: string): void {
  
    this.subscriptions.push(
      this._exportCertificateService.GetData('1', '', { vehicle_id: vehicleId, paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data && response.data.length > 0) {
            this.selectedCertificate = response.data[0];
            this.populateFormForEdit(this.selectedCertificate);
            this.isEditMode = true;
          }
          else{
            this.populateForm(this.selectedVehicle);
          }
        }
      })
    );
  
  }

  private resetForm(): void {
    this.exportCertificateForm.reset();
    this.selectedVehicle = null;
    this.selectedCertificate = null;
    this.isLoading = false;
    this.isEditMode = false;
  }

  onSubmit(): void {
    if (this.validateForm() && this.selectedVehicle) {
      this.isLoading = true;

      let formData: any = { ...this.exportCertificateForm.value };

      if (this.isEditMode && this.selectedCertificate) {
        // In edit mode, don't send vehicle_id
        this.updateCertificate(formData);
      } else {
        // In add mode, include vehicle_id
        formData.vehicle_id = this.selectedVehicle._id || this.selectedVehicle.id;
        this.addCertificate(formData);
      }
    } else {
      // Form validation error
      Swal.fire({
        title: 'Validation Error!',
        text: 'Please fill in all required fields before submitting.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
    }
  }

  private addCertificate(data: any): void {
    this.subscriptions.push(
      this._exportCertificateService.AddData(data).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Export certificate has been added successfully',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.certificateAdded.emit();
            });
          } else {
            this.handleError(response.message || 'Failed to add certificate');
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.handleError(error.error?.message || 'An error occurred while adding certificate');
        }
      })
    );
  }

  private updateCertificate(data: any): void {
    const certificateId = this.selectedCertificate._id || this.selectedCertificate.id;
    this.subscriptions.push(
      this._exportCertificateService.UpdateData(certificateId, data).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Export certificate has been updated successfully',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.certificateUpdated.emit();
            });
          } else {
            this.handleError(response.message || 'Failed to update certificate');
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.handleError(error.error?.message || 'An error occurred while updating certificate');
        }
      })
    );
  }

  private handleError(message: string): void {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonText: 'OK'
    });
  }

  private validateForm(): boolean {
    // Check if vehicle is selected
    if (!this.selectedVehicle) {
      Swal.fire({
        title: 'Vehicle Required!',
        text: 'Please select a vehicle before generating the certificate.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return false;
    }

    if (this.exportCertificateForm.invalid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.exportCertificateForm.controls).forEach(key => {
        const control = this.exportCertificateForm.get(key);
        if (control) {
          control.markAsTouched();
        }
      });
      return false;
    }
    return true;
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_vh_manager_export_certificate');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  // Vehicle search methods
  private setupVehicleSearchDebounce(): void {
    this.vehicleSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchVehicles(searchTerm);
    });
  }

  onVehicleSearchInput(event: Event): void {
    // Don't allow search if vehicle is pre-selected
    if (this.isVehiclePreSelected) {
      return;
    }

    const target = event.target as HTMLInputElement;
    this.vehicleSearchTerm = target.value;
    this.selectedVehicle = null;

    if (this.vehicleSearchTerm.length >= 2) {
      this.vehicleSearchSubject.next(this.vehicleSearchTerm);
      this.showVehicleDropdown = true;
    } else {
      this.showVehicleDropdown = false;
      this.vehicleSearchResults = [];
    }
  }

  onVehicleInputFocus(): void {
    // Don't show dropdown if vehicle is pre-selected
    if (this.isVehiclePreSelected) {
      return;
    }

    if (this.vehicleSearchResults.length > 0) {
      this.showVehicleDropdown = true;
    }
  }

  onVehicleInputBlur(): void {
    setTimeout(() => {
      this.showVehicleDropdown = false;
    }, 200);
  }

  private searchVehicles(searchTerm: string): void {
    // Don't search if vehicle is pre-selected
    if (this.isVehiclePreSelected) {
      return;
    }

    if (!searchTerm || searchTerm.length < 2) {
      this.vehicleSearchResults = [];
      this.showVehicleDropdown = false;
      return;
    }

    this.isVehicleSearching = true;
    this.subscriptions.push(
      this._vehicleService.GetData('1', searchTerm, { paginate: false, search: searchTerm }).subscribe({
        next: (response: any) => {
          this.isVehicleSearching = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            this.vehicleSearchResults = response.data.data || response.data || [];
            this.showVehicleDropdown = this.vehicleSearchResults.length > 0;
          } else {
            this.vehicleSearchResults = [];
            this.showVehicleDropdown = false;
          }
        },
        error: (error: any) => {
          this.isVehicleSearching = false;
          console.error('Error searching vehicles:', error);
          this.vehicleSearchResults = [];
          this.showVehicleDropdown = false;
        }
      })
    );
  }

  selectVehicle(vehicle: any): void {
    this.selectedVehicle = vehicle;
    this.vehicleSearchTerm = this.getVehicleDisplayName(vehicle);
    this.showVehicleDropdown = false;

    // Populate form with selected vehicle data
    this.populateForm(vehicle);
  }

  private getVehicleDisplayName(vehicle: any): string {
    const parts = [];
    if (vehicle.name) parts.push(vehicle.name);
    if (vehicle.company_name) parts.push(vehicle.company_name);
    if (vehicle.year) parts.push(`(${vehicle.year})`);
    return parts.join(' ') || 'Unknown Vehicle';
  }

  getVehicleImageUrl(vehicle: any): string {
    if (vehicle.pictures && vehicle.pictures.length > 0) {
      return vehicle.pictures[0];
    }
    return 'assets/media/svg/files/blank-image.svg'; // Default placeholder image
  }

  private resetVehicleSearch(): void {
    this.vehicleSearchTerm = '';
    this.vehicleSearchResults = [];
    this.showVehicleDropdown = false;
    this.isVehicleSearching = false;
    this.selectedVehicle = null;
    this.isVehiclePreSelected = false;
  }

  getCurrentDate(): string {
    return new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  // Getter methods for form controls
  get certificate_no() { return this.exportCertificateForm.get('certificate_no'); }
  get id_no() { return this.exportCertificateForm.get('id_no'); }
  get make_model() { return this.exportCertificateForm.get('make_model'); }
  get chassis_no() { return this.exportCertificateForm.get('chassis_no'); }
}
